/**
 * clausierService — source unique du clausier électricité côté PWA.
 *
 * Appelle l'endpoint agrégé `/referentiels/clausier-electricite` (côté API rapteck)
 * qui renvoie en un seul payload l'arbre :
 *   normes → paragraphes → catégories → templates
 *   arrêtés → articles → templates
 *
 * Cache IndexedDB : 24 h + revalidation par ETag (If-None-Match → 304).
 *
 * Les services `referentielService`, `normesService` et `arretesService` délèguent
 * tous à ce service pour rester rétro-compatibles avec le code existant.
 */

import { openDB } from 'idb';
import apiService from './apiService';

const DB_NAME = 'RapteckMobile';
const DB_VERSION = 2;
const CACHE_KEY = 'clausier_electricite_v1';
const ETAG_KEY = 'clausier_electricite_etag_v1';
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24h (revalidation forcée au-delà)

const emptyTree = () => ({ normes: [], arretes: [] });

class ClausierService {
  constructor() {
    this.db = null;
    this._inflight = null; // évite plusieurs fetch concurrents
  }

  async getDB() {
    if (this.db) return this.db;
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('inspections')) db.createObjectStore('inspections', { keyPath: 'key' });
        if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings');
        if (!db.objectStoreNames.contains('referentiel')) db.createObjectStore('referentiel');
      },
    });
    return this.db;
  }

  // ---- API publique ----

  /**
   * Retourne l'arbre. Stratégie :
   *  1. Si un cache valide (< 24h) existe → on le renvoie immédiatement.
   *  2. Sinon, on revalide via ETag/If-None-Match :
   *     - 304 → on met à jour le timestamp et on renvoie le cache.
   *     - 200 → on stocke le nouvel arbre et on le renvoie.
   *  3. Si l'API échoue (offline) et qu'on a un cache (même expiré) → on le renvoie quand même.
   */
  async getTree(opts = {}) {
    const { force = false } = opts;
    const cached = await this._loadFromCache();

    if (!force && cached && this._isFresh(cached)) {
      return cached.data;
    }

    if (this._inflight) {
      // Mutualise un fetch concurrent
      try {
        return await this._inflight;
      } catch {
        // tombe dans le bloc suivant
      }
    }

    this._inflight = this._fetchAndCache({ etag: cached?.etag });
    try {
      const fresh = await this._inflight;
      return fresh;
    } catch (err) {
      console.warn('[clausierService] fetch failed, fallback cache:', err?.message || err);
      if (cached?.data) return cached.data; // offline : on tolère un cache même expiré
      throw err;
    } finally {
      this._inflight = null;
    }
  }

  /** Force un rafraîchissement (utilisé par le bouton). */
  async refresh() {
    return this.getTree({ force: true });
  }

  async clearCache() {
    try {
      const db = await this.getDB();
      await db.delete('settings', CACHE_KEY);
      await db.delete('settings', ETAG_KEY);
    } catch (err) {
      console.warn('[clausierService] clearCache error:', err?.message || err);
    }
  }

  /** Statistiques pour le bouton "Référentiel" du header. */
  async getCacheStats() {
    try {
      const db = await this.getDB();
      const cacheData = await db.get('settings', CACHE_KEY);
      if (!cacheData) {
        return { exists: false, count: 0 };
      }
      const age = Date.now() - cacheData.timestamp;
      const totals = cacheData.totals || {};
      return {
        exists: true,
        count: totals.templates_normes || 0,
        templatesNormes: totals.templates_normes || 0,
        templatesArretes: totals.templates_arretes || 0,
        normes: totals.normes || 0,
        arretes: totals.arretes || 0,
        age,
        ageHours: Math.round(age / (60 * 60 * 1000)),
        ageDays: Math.round(age / (24 * 60 * 60 * 1000)),
        timestamp: new Date(cacheData.timestamp).toLocaleString('fr-FR'),
        expired: age > CACHE_DURATION,
      };
    } catch (err) {
      console.warn('[clausierService] getCacheStats error:', err?.message || err);
      return null;
    }
  }

  // ---- internals ----

  _isFresh(cached) {
    if (!cached?.timestamp) return false;
    return Date.now() - cached.timestamp < CACHE_DURATION;
  }

  async _loadFromCache() {
    try {
      const db = await this.getDB();
      const cacheData = await db.get('settings', CACHE_KEY);
      if (!cacheData) return null;
      return cacheData;
    } catch (err) {
      console.warn('[clausierService] _loadFromCache error:', err?.message || err);
      return null;
    }
  }

  async _fetchAndCache({ etag } = {}) {
    const token = apiService.getToken();
    const apiUrl = apiService.getApiUrl();
    if (!token || !apiUrl) {
      throw new Error('Token ou API URL indisponible');
    }

    const headers = { ...apiService.getAuthHeaders() };
    if (etag) headers['If-None-Match'] = etag;

    const res = await fetch(`${apiUrl}/referentiels/clausier-electricite`, {
      method: 'GET',
      headers,
    });

    // 304 : rien n'a changé → on met juste à jour le timestamp et on garde le cache
    if (res.status === 304) {
      const existing = await this._loadFromCache();
      if (existing) {
        await this._touchCache(existing);
        return existing.data;
      }
      // Cache absent mais le serveur dit 304 (cas pathologique) → force un GET sans ETag
      return this._fetchAndCache({ etag: null });
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`HTTP ${res.status}: ${text.substring(0, 120)}`);
    }

    const json = await res.json();
    if (!json?.success) {
      throw new Error(json?.error || 'Réponse clausier invalide');
    }
    const tree = json.data || emptyTree();
    const newEtag = res.headers.get('ETag') || (json.version ? `"${json.version}"` : null);

    await this._saveToCache({
      data: tree,
      etag: newEtag,
      version: json.version || null,
      totals: json.totals || null,
      generated_at: json.generated_at || null,
      timestamp: Date.now(),
    });

    return tree;
  }

  async _saveToCache(payload) {
    try {
      const db = await this.getDB();
      await db.put('settings', payload, CACHE_KEY);
    } catch (err) {
      console.warn('[clausierService] _saveToCache error:', err?.message || err);
    }
  }

  async _touchCache(existing) {
    try {
      const db = await this.getDB();
      await db.put('settings', { ...existing, timestamp: Date.now() }, CACHE_KEY);
    } catch (err) {
      console.warn('[clausierService] _touchCache error:', err?.message || err);
    }
  }
}

const clausierService = new ClausierService();
export default clausierService;

/* ============================================================================
   Helpers de transformation pour les services rétro-compatibles.
   ============================================================================ */

/**
 * Aplatit l'arbre Normes en une liste plate de templates (format historique
 * attendu par `referentielService.getReferentiel()` et tout le code existant).
 */
export function flattenNormeTemplates(tree) {
  const out = [];
  for (const norme of tree?.normes || []) {
    for (const para of norme.paragraphes || []) {
      for (const cat of para.categories || []) {
        for (const tpl of cat.templates || []) {
          out.push({
            id: tpl.id,
            titre: tpl.titre || '',
            constat: tpl.constat || '',
            reference_reglementaire: tpl.reference_reglementaire || '',
            niveau_conformite: tpl.gravite || 'Non-conformité',
            actif: true,
            norme_id: norme.id,
            norme_code: norme.code,
            norme_titre: norme.titre,
            paragraphe_code: para.code,
            paragraphe_titre: para.titre,
            categorie_code: cat.code,
            categorie_titre: cat.titre,
            code: tpl.code || '',
            domaine: norme.domaine || 'BT',
            lien_etape_14: tpl.lien_etape_14 || '',
            annee: norme.annee,
          });
        }
      }
    }
  }
  return out;
}

/**
 * Extrait la liste plate des normes au format attendu par `normesService.getListeNormes()`.
 */
export function extractNormesList(tree) {
  return (tree?.normes || []).map((n) => ({
    id: n.id,
    code: n.code,
    titre: n.titre,
    domaine: n.domaine,
    annee: n.annee,
    ordre: n.ordre,
    actif: true,
  }));
}

/**
 * Reconstruit la structure { articles, templates, byArreteCode, byArticleId }
 * historiquement renvoyée par `arretesService.list()`.
 */
export function buildArretesIndex(tree) {
  const articles = [];
  const templates = [];
  const byArreteCode = new Map();
  const byArticleId = new Map();

  for (const arrete of tree?.arretes || []) {
    const articlesArrete = arrete.articles || [];
    if (articlesArrete.length === 0) continue;

    byArreteCode.set(arrete.code, articlesArrete.map((art) => ({
      id: art.id,
      arrete_code: arrete.code,
      article_numero: art.article_numero,
      prescription: art.prescription,
      code_travail: art.code_travail,
      ordre: art.ordre,
      actif: true,
    })));

    for (const art of articlesArrete) {
      const articleFlat = {
        id: art.id,
        arrete_code: arrete.code,
        article_numero: art.article_numero,
        prescription: art.prescription,
        code_travail: art.code_travail,
        ordre: art.ordre,
        actif: true,
      };
      articles.push(articleFlat);

      const articleTemplates = (art.templates || []).map((t) => ({
        id: t.id,
        article_arrete_id: art.id,
        titre: t.titre,
        constat: t.constat,
        reference_reglementaire: t.reference_reglementaire,
        ordre: t.ordre,
        actif: true,
      }));
      for (const t of articleTemplates) templates.push(t);
      byArticleId.set(art.id, articleTemplates);
    }
  }

  return { articles, templates, byArreteCode, byArticleId };
}
